"use client";

import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useFetchData } from "../../../hooks/useFetch";
import { isSuccess } from "../../../models/response/base-response";
import {
  ApiConfig,
  API_ORDER_EMPLOYEES_ROUTER,
  API_USER_ROUTER,
} from "../../../services/api-config";
import BranchService, { Branch } from "../../branch/services/branch-service";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import EmployeeKgDetailModal from "../components/EmployeeKgDetailModal";
import Select from "react-select";
import { showToast } from "../../../components/custom/custom-toast";
import { downloadOrderServiceExcel } from "../utils/download-excel";

interface KgReportRow {
  employee_id: number;
  employee_name: string;
  branch_count?: number; // số chi nhánh NV này đã làm (xem breakdown ở modal chi tiết)
  // Chỉ 4 vai được tính sản lượng: nhận hàng · giặt · giao · kiểm đếm.
  // Sấy/ủi/gấp/vận chuyển vẫn được ghi công trong đơn nhưng không lên báo cáo này (lọc ở BE).
  pickup_kg: number;
  wash_kg: number;
  delivery_kg: number;
  count_kg: number;
  // Phần làm bằng XE TẢI ĐI THẲNG — ĐÃ nằm trong pickup_kg / delivery_kg (hiện dòng phụ, không cộng thêm).
  // Công trung chuyển (vai 10/11) CHƯA lên báo cáo này — xem user/docs/xe-tai-doi-hinh-ky-thuat.md §8.
  truck_pickup_kg?: number;
  truck_delivery_kg?: number;
  total_kg: number;
  order_count: number;
}

/** Ô kg kèm dòng phụ "xe tải: x" khi có phần làm bằng xe tải đi thẳng. */
const KgWithTruck = ({ kg, truckKg }: { kg: number; truckKg?: number }) => (
  <>
    {formatKg(kg)}
    {Number(truckKg) > 0 && (
      <span className="block text-[11px] font-normal text-amber-600">xe tải: {formatKg(Number(truckKg))}</span>
    )}
  </>
);

interface SimpleUser {
  id: number;
  name: string;
}

function extractList(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw?.list && Array.isArray(raw.list)) return raw.list;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  return [];
}

// Làm tròn tối đa 1 chữ số thập phân, bỏ ".0" thừa: 23→"23", 11.53→"11.5".
const round1 = (v: number) => Math.round((Number(v) || 0) * 10) / 10;
const formatKgValue = (v: number) => String(round1(v));            // card tổng: 0 hiện "0"
const formatKg = (v: number) => (Number(v) > 0 ? String(round1(v)) : "-"); // ô bảng: 0 hiện "-"
const formatNumber = (n: number) => n.toLocaleString("vi-VN");

// DateInputCustom dùng dd/MM/yyyy; BE report so sánh chuỗi YYYY-MM-DD → convert ở ranh giới.
const toDMY = (ymd: string) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return d && m && y ? `${d}/${m}/${y}` : "";
};
const toYMD = (dmy: string) => {
  if (!dmy) return "";
  const [d, m, y] = dmy.split("/");
  return d && m && y ? `${y}-${m}-${d}` : "";
};

const getDefaultDates = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
  return { from: fmt(firstDay), to: fmt(now) };
};

const EmployeeKgReportPage: React.FC = () => {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.from);
  const [endDate, setEndDate] = useState(defaults.to);
  const [selectedBranchId, setSelectedBranchId] = useState<number | "">("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | "">("");
  const [rows, setRows] = useState<KgReportRow[]>([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [serverSummary, setServerSummary] = useState<KgReportRow | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [branches, setBranches] = useState<Branch[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [employees, setEmployees] = useState<SimpleUser[]>([]);
  // Modal chi tiết 1 nhân viên
  const [detailEmp, setDetailEmp] = useState<{ id: number; name: string } | null>(null);

  const { request: fetchReport } = useFetchData.Get<any>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });
  // Chỉ chi nhánh user được phụ trách (không lấy toàn công ty).
  const { fetch: listBranches } = BranchService.myBranches();
  const { request: listUsers } = useFetchData.Get<any>({
    projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
  });

  const fetchRef = useRef(fetchReport);
  const branchRef = useRef(listBranches);
  const userRef = useRef(listUsers);

  useEffect(() => { fetchRef.current = fetchReport; }, [fetchReport]);
  useEffect(() => { branchRef.current = listBranches; }, [listBranches]);
  useEffect(() => { userRef.current = listUsers; }, [listUsers]);

  useEffect(() => {
    (async () => {
      const res = await branchRef.current();
      if (res && isSuccess(res)) setBranches(extractList(res.data));
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const res = await userRef.current({
        endPoint: API_USER_ROUTER.API_LIST,
        data: { limit: 500, status: 1, company_id: 2000 },
      });
      if (res && isSuccess(res)) setAllUsers(extractList(res.data));
    })();
  }, []);

  useEffect(() => {
    // Nhân viên chỉ trong phạm vi chi nhánh phụ trách (myBranches). Chọn 1 chi nhánh → chỉ NV chi nhánh đó.
    const myBranchIds = new Set(branches.map((b: any) => Number(b.id)));
    const inScope = (u: any) =>
      u.is_all_branch === 1 || (u.branches || []).some((b: any) => myBranchIds.has(Number(b.id)));
    const filtered = allUsers.filter((u: any) => {
      if (!inScope(u)) return false;
      if (!selectedBranchId) return true;
      return u.is_all_branch === 1 || (u.branches || []).some((b: any) => Number(b.id) === Number(selectedBranchId));
    });
    setEmployees(filtered.map((u: any) => ({ id: u.id, name: u.name })));
    setSelectedEmployeeId("");
  }, [selectedBranchId, allUsers, branches]);

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = { page, limit: pageSize };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedBranchId) params.branch_id = selectedBranchId;
      if (selectedEmployeeId) params.employee_id = selectedEmployeeId;
      const res = await fetchRef.current({
        endPoint: API_ORDER_EMPLOYEES_ROUTER.API_KG_REPORT,
        data: params,
      });
      if (res && isSuccess(res)) {
        setRows(res.data?.list ?? []);
        setTotalRecord(res.data?.total_record ?? 0);
        setServerSummary(res.data?.summary ?? null);
      }
    } catch (e) {
      console.error("Failed to load kg report:", e);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, selectedBranchId, selectedEmployeeId, page, pageSize]);

  // Reset page when filters change (not when page changes)
  useEffect(() => { setPage(1); }, [startDate, endDate, selectedBranchId, selectedEmployeeId]);

  useEffect(() => { void loadReport(); }, [loadReport]);

  // Summary cards from server summary
  const summary = useMemo(() => {
    if (!serverSummary) return { totalKg: 0, totalOrders: 0, totalEmployees: 0, avgKg: 0 };
    const totalKg = serverSummary.total_kg;
    const totalOrders = serverSummary.order_count;
    const totalEmployees = totalRecord;
    const avgKg = totalEmployees > 0 ? totalKg / totalEmployees : 0;
    return { totalKg, totalOrders, totalEmployees, avgKg };
  }, [serverSummary, totalRecord]);

  // Xuất Excel: BE trả TOÀN BỘ nhân viên theo bộ lọc đang xem (không chỉ trang hiện tại).
  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    setExporting(true);
    try {
      const params: Record<string, unknown> = { start_date: startDate, end_date: endDate };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      if (selectedEmployeeId) params.employee_id = selectedEmployeeId;
      await downloadOrderServiceExcel(
        API_ORDER_EMPLOYEES_ROUTER.API_KG_REPORT_EXPORT,
        params,
        `san-luong-nhan-vien_${startDate}_${endDate}.xlsx`,
      );
    } catch (e: any) {
      showToast({ message: e?.message || "Xuất Excel thất bại", type: "error" });
    } finally {
      setExporting(false);
    }
  };

  // Pagination
  const totalPages = Math.max(1, Math.ceil(totalRecord / pageSize));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Báo cáo sản lượng nhân viên</h1>
              <p className="text-sm text-gray-500 mt-0.5">Tổng hợp số kg xử lý theo từng nhân viên</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportExcel}
              disabled={exporting || !startDate || !endDate}
              className="flex items-center gap-2 h-10 px-4 bg-green-600 text-white text-sm font-medium rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {exporting ? "Đang xuất..." : "Xuất Excel"}
            </button>
            <button
              onClick={loadReport}
              disabled={isLoading}
              className="flex items-center justify-center w-10 h-10 bg-white border border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <svg className={`w-5 h-5 text-gray-600 ${isLoading ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Từ ngày</label>
              <DateInputCustom
                type="date"
                value={toDMY(startDate)}
                onChange={(d) => setStartDate(toYMD(d ?? ""))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Đến ngày</label>
              <DateInputCustom
                type="date"
                value={toDMY(endDate)}
                onChange={(d) => setEndDate(toYMD(d ?? ""))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Chi nhánh</label>
              <select value={selectedBranchId} onChange={(e) => setSelectedBranchId(e.target.value ? Number(e.target.value) : "")}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white transition-colors">
                <option value="">Tất cả chi nhánh phụ trách</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Nhân viên</label>
              <Select
                placeholder="Tất cả nhân viên"
                options={employees.map((u) => ({ label: u.name, value: u.id }))}
                value={
                  selectedEmployeeId
                    ? {
                        label: employees.find((u) => u.id === selectedEmployeeId)?.name,
                        value: selectedEmployeeId,
                      }
                    : null
                }
                onChange={(option: any) => setSelectedEmployeeId(option ? option.value : "")}
                isClearable
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "0.75rem",
                    borderColor: "#e5e7eb",
                    minHeight: "42px",
                    boxShadow: "none",
                    "&:hover": { borderColor: "#3b82f6" },
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: "0 8px",
                  }),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <p className="text-sm font-medium text-gray-500">Tổng số kg</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatKgValue(summary.totalKg)} <span className="text-sm font-normal text-gray-400">kg</span></p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <p className="text-sm font-medium text-gray-500">Tổng số đơn</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatNumber(summary.totalOrders)}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <p className="text-sm font-medium text-gray-500">Nhân viên</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary.totalEmployees}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <p className="text-sm font-medium text-gray-500">TB kg / nhân viên</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatKgValue(summary.avgKg)} <span className="text-sm font-normal text-gray-400">kg</span></p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Chi tiết theo nhân viên</h2>
          <span className="text-sm text-gray-400">{totalRecord} nhân viên</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-center font-semibold text-gray-600 w-12">#</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 min-w-[140px]">Nhân viên</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">Nhận hàng</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">Giặt</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">Giao</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">Kiểm đếm</th>
                <th className="px-4 py-3 text-right font-semibold text-blue-600 bg-blue-50/50">Tổng (kg)</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Đơn</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600"></th>
              </tr>
              {/* Tổng cộng ở trên (từ server summary - tổng tất cả, không chỉ trang hiện tại) */}
              {serverSummary && totalRecord > 0 && (
                <tr className="bg-blue-50/60 border-b-2 border-blue-200 font-bold">
                  <td colSpan={2} className="px-4 py-2.5 text-right text-blue-800 text-xs uppercase tracking-wide">Tổng cộng ({totalRecord} NV)</td>
                  <td className="px-4 py-2.5 text-right text-blue-800"><KgWithTruck kg={serverSummary.pickup_kg} truckKg={serverSummary.truck_pickup_kg} /></td>
                  <td className="px-4 py-2.5 text-right text-blue-800">{formatKg(serverSummary.wash_kg)}</td>
                  <td className="px-4 py-2.5 text-right text-blue-800"><KgWithTruck kg={serverSummary.delivery_kg} truckKg={serverSummary.truck_delivery_kg} /></td>
                  <td className="px-4 py-2.5 text-right text-blue-800">{formatKg(serverSummary.count_kg)}</td>
                  <td className="px-4 py-2.5 text-right text-blue-900 bg-blue-100/50 font-extrabold">{formatKg(serverSummary.total_kg)}</td>
                  <td className="px-4 py-2.5 text-center text-blue-800">{formatNumber(serverSummary.order_count)}</td>
                  <td className="px-4 py-2.5"></td>
                </tr>
              )}
            </thead>
            <tbody className="divide-y divide-gray-50">
              {isLoading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <svg className="animate-spin w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span className="text-gray-400 text-sm">Đang tải...</span>
                  </div>
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">Không có dữ liệu</td></tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.employee_id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-center text-gray-400">{(page - 1) * pageSize + i + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {row.employee_name}
                      {Number(row.branch_count) > 1 && (
                        <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-orange-50 text-orange-700">
                          {row.branch_count} chi nhánh
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600"><KgWithTruck kg={row.pickup_kg} truckKg={row.truck_pickup_kg} /></td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatKg(row.wash_kg)}</td>
                    <td className="px-4 py-3 text-right text-gray-600"><KgWithTruck kg={row.delivery_kg} truckKg={row.truck_delivery_kg} /></td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatKg(row.count_kg)}</td>
                    <td className="px-4 py-3 text-right font-bold text-blue-700 bg-blue-50/30">{formatKg(row.total_kg)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                        {row.order_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setDetailEmp({ id: row.employee_id, name: row.employee_name })}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-blue-600 border border-blue-200 hover:bg-blue-50 transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        Chi tiết
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalRecord > pageSize && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Hiển thị {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalRecord)} / {totalRecord}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                «
              </button>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce<(number | string)[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  typeof p === "string" ? (
                    <span key={`dot-${idx}`} className="px-1 text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                        p === page
                          ? "bg-blue-500 text-white border-blue-500"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                ›
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal chi tiết 1 nhân viên (theo bộ lọc hiện tại) */}
      {detailEmp && (
        <EmployeeKgDetailModal
          employeeId={detailEmp.id}
          employeeName={detailEmp.name}
          branchId={selectedBranchId}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setDetailEmp(null)}
        />
      )}
    </div>
  );
};

export default EmployeeKgReportPage;
